import * as _ from 'lodash';
import * as api from './../../api/api.service';
import * as featuresApi from './../../api/features.service';
import * as rfpsApi from './../../api/rfps/rfp.service';
import { ApplicationPermission } from './../../api/users.service';
import { IAptAttachment } from '../../core/layout/fileUpload/aptFileAttachments.service';
import BuyerOfferImpactService from './buyerOfferImpact.service';
import { SizeOfSpendTypeLabel } from '../../core/util/spendLevelType.const';
import { Offer } from '../../api/offers.service';

export interface Seller {
  id: number;
  name: string;
  offerId: number;
  offerName: string;
  targetMarketShare: number;
  permissions: ApplicationPermission[];
}

interface MemberSummary {
  id: number;
  name: string;
  combinedMarketShare: number;
  memberPlanId: number;
  sellerImpacts: any;
}

interface MemberWithPuckDetails extends MemberSummary {
  // id: number;
  // name: string;
  // combinedMarketShare: number;
  // memberPlanId: number;
  plannedSpend: number;
  savings: number;
  continuedSpend: number;
  meetsTargetMarketShare: boolean;
  allocatedMarketShare: number;
  targetMarketShare: number;
  activeSellerImpact: any;
  conversionsNeededCount: number;
  inclusionModifiedBy?: string;
  activeSeller?: any;
}

type Member = MemberSummary | MemberWithPuckDetails;

interface MasterPlan {
  aggregationGroupName;
  categoryName;
  categoryId;
  requestId;
  rfpType;
  sellerInfo;
  memberImpact;
}

class OfferImpactForBuyerCtrl {
  static bindings = {
    navName: '@', // a string value indicating which nav to use - purchasePlan or requestForPricing are currently used in template
    rfpId: '<',
    sellerId: '<',
  };

  // Bindings
  purchasePlan;
  purchasePlanId;
  sellerId;

  titleAndSubtitle = {
    title: 'Offer Impact',
    subtitle: '',
  };

  rfpId;

  account;
  featureList: featuresApi.FeatureFlags;
  rfp: rfpsApi.RfpVersionTwo;
  masterPlanId;
  masterPlan: MasterPlan;
  activePurchasePlan;
  members: MemberWithPuckDetails[]; // The list of members which we display as pucks
  permissions; // global permissions for this page
  sellers: Seller[]; // was dataSets // the list of sellers which we display as tabs across the top
  activeSeller: Seller; // was activeDataSet

  // Was on dataSets ... get it off this.masterPlan now
  // aggregationGroupName: masterPlan.aggregationGroupName,
  // categoryName: masterPlan.categoryName,
  // categoryId: masterPlan.categoryId,
  // requestId: masterPlan.requestId,
  // rfpType: masterPlan.rfpType,

  // We use this to track when the members list updates so we can re-calculate numbers
  // cachedMembers;

  // This is the list we actually use to calculate the numbers
  // This is generated from the list of members for the active seller when they update
  sellerImpacts: any[];
  puckApi;
  attachments: IAptAttachment[];
  draftRfpPermissions;
  sizeOfSpendTypeLabel = SizeOfSpendTypeLabel;
  isPartnerLevel = false;

  constructor(
    private api: api.IApiService,
    private LoadingIndicator,
    private $q,
    private OfferImpactService,
    private $location,
    private $scope,
    PurchasePlanningPermissions,
    private BuyerOfferImpact: BuyerOfferImpactService,
    private OptAllInModal,
    private SaveService,
  ) {
    'ngInject';

    this.permissions = PurchasePlanningPermissions;

    this.BuyerOfferImpact.setPurchasePlanningPermissions(this.permissions);
  }

  initPuckApi($event) {
    if ($event.api) {
      this.puckApi = $event.api;
    }
  }

  /**
   * This loads the account, rfp, and other data
   */
  $onInit() {
    this.$q
      .all([
        // Load the global permissions (we also have seller-specfic permissions)
        this.BuyerOfferImpact.loadAccount(),
        this.BuyerOfferImpact.loadFeatures(),
        this.api.rfps.getActivePurchasePlan(this.rfpId),
      ])
      .then((responses) => {
        [this.account, this.featureList, this.purchasePlan] = responses;
        this.purchasePlanId = this.purchasePlan.id;
        this.checkDisablePPDetail(this.purchasePlan);
        this.api.users
          .applicationPermissions(this.account.userId, 'BUYER_RFP', this.rfpId)
          .then((permissions) => {
            this.draftRfpPermissions = permissions;
          });

        this.api.users
          .loadPurchasePlanningPermissions(
            this.account.userId,
            this.purchasePlanId,
          )
          .then((permissions) => {
            this.permissions.setPermissions(permissions);
          });

        // Find out which purchasePlanningId is the master plan ID in case the
        const masterPlanIdPromise = this.api.purchasePlans
          .getMasterPlanId(this.purchasePlanId)
          .then(this.postMasterPlanIdLoad.bind(this));

        this.LoadingIndicator.waitFor(masterPlanIdPromise);
      });
  }

  getIsParticipantsImpact(activePlan, featureList) {
    return (
      activePlan?.purchasePlanningLevel &&
      activePlan?.purchasePlanningLevel === 'SOLO_LEVEL_PLANNING'
    );
  }

  postMasterPlanIdLoad(masterPlanId) {
    this.masterPlanId = masterPlanId.id;

    return this.api.purchasePlans
      .getMasterPlanInfo(this.masterPlanId)
      .then((masterPlan) => {
        this.masterPlan = masterPlan;
        const sellers = this.BuyerOfferImpact.mapSellers(masterPlan.sellerInfo);

        // Display sellers alphabetically
        this.sellers = this.BuyerOfferImpact.sortSellersByName(sellers);

        // Set the active tab
        if (this.sellerId) {
          this.setActiveSellerById(this.sellerId);
        } else {
          this.setActiveSeller(this.sellers[0]);
        }

        this.rfpId = this.masterPlan.requestId;

        this.api.rfps.getActivePurchasePlan(this.rfpId).then((response) => {
          this.activePurchasePlan = response;
        });

        // Why would this be the case?
        // Once we've gotten all the puck information, we can ask about the user's permissions for the page.
        // Attempting to do so before this point may cause the back-end to return incorrect results.
        _.each(this.sellers, (seller) => {
          // Permissions used to be blanket for the entire page, but not anymore, we need to be looking at
          // permissions as a per/seller kind of thing. That's because we could have signed for one seller and
          // not for another.
          this.api.users
            .getBuyerImpactPermissions(this.masterPlanId, seller.id)
            .then((permissions) => {
              seller.permissions = permissions;
            });
        });
        // Get the list of members from the master plan
        // The core list of members won't change when the active seller changes -- only the spend data will change
        this.members = this.BuyerOfferImpact.mapMembers(
          this.masterPlan.memberImpact,
        ) as MemberWithPuckDetails[];

        // Do an initial sort so that we load member data from top first.
        this.members = _.sortBy(this.members, 'name');

        return this.BuyerOfferImpact.loadRfp(this.rfpId).then((rfp) => {
          this.rfp = rfp;
          // Gets RFP attachments from buyer
          this.api.rfps.getAttachments(this.rfp.id).then((attachments) => {
            this.attachments = attachments;
          });

          // Iterate through all members to request their spend data and update the pucks.
          _.each(this.members, (member) => {
            this.BuyerOfferImpact.getMemberDataAndUpdateSpend(
              member,
              this.masterPlanId,
              this.activeSeller,
            );
          });
        });
      });
  }

  isSellerActive(seller) {
    return seller === this.activeSeller;
  }

  setActiveSeller(seller) {
    if (this.puckApi) {
      this.puckApi.closePuck();
    }

    this.activeSeller = seller;

    this.titleAndSubtitle.subtitle =
      'For ' + seller.aggregationGroupName + ' In ' + seller.categoryName;

    this.updateAllMemberSpendForSeller(this.activeSeller);
  }

  updateAllMemberSpendForSeller(activeSeller) {
    _.each(this.members, (member) => {
      this.BuyerOfferImpact.updateMemberSpendForSeller(member, activeSeller);
    });
  }

  setActiveSellerById(sellerId) {
    this.setActiveSeller(
      _.find(this.sellers, {
        id: parseInt(sellerId, 10),
      }),
    );
  }

  // Get the activeSeller tier Type
  getActiveSellerTierTypeString() {
    if (!this.activePurchasePlan) {
      return '';
    }

    return this.getActiveSellerOffer()?.plannedSpendLevel.type.displayName;
  }

  getActiveSellerSelectedTierName(): string {
    const activeSellerOffer = this.getActiveSellerOffer() || {};
    return activeSellerOffer.selectedTier
      ? activeSellerOffer.selectedTier.name
      : '';
  }

  getOfferById(offerId: number): Offer {
    return this.activePurchasePlan
      ? this.activePurchasePlan.offers.find((offer) => offer.id == offerId)
      : ({} as Offer);
  }

  getSelectedTierByOfferId(offerId: number) {
    return this.getOfferById(offerId).selectedTier;
  }

  getActiveSellerOffer() {
    return this.activePurchasePlan
      ? this.activePurchasePlan.offers.find(
          (offer) => offer.id == this.activeSeller.offerId,
        )
      : {};
  }

  // Get permissions data -- the old one
  getOptAllInDisabledStatus() {
    if (
      this.activeSeller &&
      this.activeSeller.permissions &&
      this.members &&
      (this.activeSeller.permissions as any).OPT_IN_OUT &&
      this.isLeader()
    ) {
      // The meet all of the permissions criteria to opt in/out.
      return false;
    } else {
      return true;
    }
  }

  allIn(inclusionStatus = 'In') {
    return _.every(this.members, (member: any) => {
      return (
        member.activeSellerImpact &&
        (member.activeSellerImpact.inclusionStatus === inclusionStatus ||
          member.activeSellerImpact.inclusionStatus == null)
      );
    });
  }

  allOut() {
    return this.allIn('Out');
  }

  setAllIn(included: boolean) {
    this.OptAllInModal.openModal(included).then(() => {
      // Call the api to update the status, then update our local copy to reflect the change
      this.BuyerOfferImpact.setAllSystemsToIn(
        this.masterPlanId,
        this.activeSeller.id,
        included,
      ).then(() => {
        // Close any open pucks before updating the status so that it can resort properly
        if (this.puckApi) {
          this.puckApi.closePuck();
        }

        // Cycle through the local data structure and update it.
        _.each(this.members, (member: any) => {
          member.activeSellerImpact.inclusionStatus = included ? 'In' : 'Out';
        });
      });
    });
  }

  isLeader() {
    return this.account && this.account.isLeader;
  }

  // This occurs when a partner is viewing a group plan with multiple published plans and no active plan.
  // TODO: We do this in several places. Move to a shared service
  // This logic was copied from the details page -- but on this page can we ever hit this scenario?
  isNoActivePlanLoaded(): boolean {
    return this.activePurchasePlan && this.activePurchasePlan.basedOn == null;
  }

  canSave(): boolean {
    return this.permissions.canSavePlan() && !this.isNoActivePlanLoaded();
  }

  // Save and Continue are both buttons on the Sub-Nav the user can click to take an action on this page.
  save() {
    this.SaveService.save(this.rfp.id, '', null, true);
  }

  continueToSummary() {
    if (!this.isPartnerLevel) {
      this.$location.path(`/summary/${this.rfp.id}`);
    } else if (this.isPartnerLevel && !this.account.isPartner) {
      this.$location.path(`/compareRfi/${this.rfp.id}`);
    } else if (this.isPartnerLevel && this.account.isPartner) {
      this.$location.path(`/summary/${this.rfp.id}`);
    }
  }

  checkDisablePPDetail(purchasePlan) {
    this.isPartnerLevel =
      purchasePlan?.purchasePlanningLevel === 'PARTNER_LEVEL_PLANNING';
  }

  checkIsPublishedPlanFlag(): boolean {
    return !this.account.isPartner;
  }
}

export const OfferImpactForBuyerComponent = {
  template: require('./offerImpactForBuyer.component.html'),
  controller: OfferImpactForBuyerCtrl,
  bindings: OfferImpactForBuyerCtrl.bindings,
};
